// src/components/MainLayout.jsx
import { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import logo from '../assets/tu_logo.png';

import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  List as List2,
  ListItemAvatar,
  Avatar as Avatar2,
  Paper,
  Zoom,
  Slide,
  Fade,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';

// Iconos
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InstagramIcon from '@mui/icons-material/Instagram';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SessionTimeout from './SessionTimeout';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoIcon from '@mui/icons-material/Info';
import DuoIcon from '@mui/icons-material/Duo';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import PinterestIcon from '@mui/icons-material/Pinterest';
import XIcon from '@mui/icons-material/X';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';

// Estado de suscripciones
import EstadoSuscripcion from './EstadoSuscripcion';
import EstadoSuscripcionClinica from './EstadoSuscripcionClinica';

import { useClinic } from '../context/ClinicContext';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import ClinicSwitcher from './ClinicSwitcher';
import CreateClinicDialog from './CreateClinicDialog';

// Variables de entorno para control de features
const ENABLE_CLINIC_SWITCHER = import.meta.env.VITE_ENABLE_CLINIC_SWITCHER === 'true';
const ENABLE_CREATE_CLINIC = import.meta.env.VITE_ENABLE_CREATE_CLINIC === 'true';

const drawerWidth = 252;
const base = '/doctor';

// CAMBIO 2: Keyframe para animación pulse del theme switcher
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

function getDisplayName(user) {
  if (!user) return 'Usuario';
  const first = user.first_name ?? user.firstName ?? '';
  const last = user.last_name ?? user.lastName ?? '';
  const full = `${first} ${last}`.trim();
  return full || user.username || user.email || 'Usuario';
}

const MainLayout = () => {
  const { user, loading, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { memberships, selectedClinicId, setSelectedClinicId, activeClinic } = useClinic();
  const myMembership = memberships?.find(m => m.clinica?.id === selectedClinicId);
  const isClinicAdmin = !!myMembership && myMembership.rol === 'Administrador';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileClinicSheetOpen, setMobileClinicSheetOpen] = useState(false);

  // Features individuales
  const individualFeatures = useSubscriptionFeatures();
  // Features de clínica
  const clinicFeatures = useClinicFeatures();

  // Decidir según contexto (clínica vs individual)
  const tieneAccesoFinanzas = selectedClinicId
    ? clinicFeatures.hasFinanzasAccess
    : individualFeatures.hasFinanzasAccess;

  const tieneAccesoRecordatorios = selectedClinicId
    ? clinicFeatures.hasRecordatoriosAccess
    : individualFeatures.hasRecordatoriosAccess;

  const tieneAccesoReportes = selectedClinicId
    ? clinicFeatures.hasReportesAvanzados
    : individualFeatures.hasPdfAccess;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Backspace') {
        const target = event.target;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
          return;
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    toast.info('Has cerrado la sesión.');
    navigate('/LandingPage');
  };

  // Helpers de rol/género
  const isMedico = () => {
    const rol = (user?.rol || user?.role || '').toLowerCase();
    const hasMedicoProfile = !!user?.medico;
    const roleIsDoctor = rol === 'médico' || rol === 'medico' || rol === 'doctor';
    return roleIsDoctor || hasMedicoProfile;
  };

  const isEnfermeria = () => {
    const rol = (user?.rol || user?.role || '').toLowerCase();
    return rol.includes('enfermer') || rol === 'enfermería';
  };

  const getProfessionalTitle = () => {
    if (isMedico()) {
      const genero = (user?.medico?.genero || user?.gender || '').toLowerCase();
      return genero === 'femenino' || genero === 'f' || genero === 'female' ? 'Dra.' : 'Dr.';
    }
    if (isEnfermeria()) return 'Lic.';
    return '';
  };

  function getUserLabel(user, titleFn) {
    const name = getDisplayName(user);
    const title = typeof titleFn === 'function' ? titleFn() : '';
    return `${title ? title + ' ' : ''}${name}`.trim();
  }

  // Estilos de navegación con microinteracciones
  const navItemStyles = {
    py: 1.2,
    px: 2.5,
    mb: 1,
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 160ms ease, transform 140ms ease, box-shadow 160ms ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateX(2px)',
      boxShadow: theme.shadows[1],
    },
    '&.Mui-selected': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.light, 0.16)
          : alpha(theme.palette.primary.main, 0.12),
      boxShadow: theme.shadows[2],
      '&:hover': {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.light, 0.24)
            : alpha(theme.palette.primary.main, 0.16),
      },
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: 'primary.main',
        fontWeight: 600,
      },
      border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
    },
    '& .MuiTouchRipple-root span': {
      backgroundColor: alpha(theme.palette.primary.main, 0.26),
    },
  };

  // Sidebar con tarjeta elevada para el header
  const drawerHeader = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        boxShadow: 'none',
        bgcolor: 'transparent',
      }}
    >
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <RouterLink
          to={`${base}/dashboard`}
          style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
        >
          <Fade in timeout={300}>
            <img src={logo} alt="Logo Medify" style={{ height: '40px', marginRight: '12px', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }} />
          </Fade>
          <Typography variant="h6" fontWeight="bold">
            MEDIFY
          </Typography>
        </RouterLink>
      </Toolbar>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Zoom in timeout={260}>
          <Box>
            {selectedClinicId ? <EstadoSuscripcionClinica /> : <EstadoSuscripcion />}
          </Box>
        </Zoom>
      </Box>
    </Paper>
  );

  // Lista principal con pequeñas secciones encapsuladas
  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box>{drawerHeader}</Box>

      <List sx={{ p: 2 }}>
        <Typography variant="caption" sx={{ px: 1, color: 'text.secondary', fontWeight: 'medium' }}>
          General
        </Typography>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/dashboard`}
            sx={navItemStyles}
            selected={location.pathname === `${base}/dashboard`}
          >
            <ListItemIcon><DashboardIcon /></ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/citas`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/citas`)}
          >
            <ListItemIcon><EventAvailableIcon /></ListItemIcon>
            <ListItemText primary="Calendario" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/pacientes`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/pacientes`)}
          >
            <ListItemIcon><PeopleIcon /></ListItemIcon>
            <ListItemText primary="Pacientes" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/telemedicina`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/telemedicina`)}
          >
            <ListItemIcon><DuoIcon /></ListItemIcon>
            <ListItemText primary="Telemedicina" />
          </ListItemButton>
        </ListItem>

        <Typography
          variant="caption"
          sx={{ px: 1, mt: 2, color: 'text.secondary', fontWeight: 'medium', display: 'block' }}
        >
          Gestión
        </Typography>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/perfil/medico`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/perfil/medico`)}
          >
            <ListItemIcon><AccountCircleIcon /></ListItemIcon>
            <ListItemText primary="Mi Perfil" />
          </ListItemButton>
        </ListItem>

        {activeClinic && isClinicAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={`${base}/perfil/clinica`}
              sx={navItemStyles}
              selected={location.pathname.startsWith(`${base}/perfil/clinica`)}
            >
              <ListItemIcon><BusinessIcon /></ListItemIcon>
              <ListItemText primary="Perfil de la Clínica" />
            </ListItemButton>
          </ListItem>
        )}

        {isClinicAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={`${base}/clinica/miembros`}
              sx={navItemStyles}
              selected={location.pathname.startsWith(`${base}/clinica/miembros`)}
            >
              <ListItemIcon><PeopleIcon /></ListItemIcon>
              <ListItemText primary="Miembros" />
            </ListItemButton>
          </ListItem>
        )}

        <Tooltip title={!tieneAccesoFinanzas ? 'Disponible solo en planes de pago' : ''} placement="right">
          <span>
            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to={`${base}/finanzas`}
                sx={navItemStyles}
                selected={location.pathname.startsWith(`${base}/finanzas`)}
                disabled={!tieneAccesoFinanzas}
              >
                <ListItemIcon><MonetizationOnIcon /></ListItemIcon>
                <ListItemText primary="Finanzas" />
              </ListItemButton>
            </ListItem>
          </span>
        </Tooltip>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/planes`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/planes`)}
          >
            <ListItemIcon><SubscriptionsIcon /></ListItemIcon>
            <ListItemText primary="Suscripción" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/ayuda`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/ayuda`)}
          >
            <ListItemIcon><HelpOutlineIcon /></ListItemIcon>
            <ListItemText primary="Ayuda" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={`${base}/acerca-de`}
            sx={navItemStyles}
            selected={location.pathname.startsWith(`${base}/acerca-de`)}
          >
            <ListItemIcon><InfoIcon /></ListItemIcon>
            <ListItemText primary="Acerca de" />
          </ListItemButton>
        </ListItem>
      </List>

      {isMedico() && (
        <>
          <Typography
            variant="caption"
            sx={{ px: 3, mt: 2, color: 'text.secondary', fontWeight: 'medium', display: 'block' }}
          >
            Catálogos
          </Typography>

          <List sx={{ p: 2 }}>
            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to={`${base}/catalogo`}
                sx={navItemStyles}
                selected={location.pathname.startsWith(`${base}/catalogo`)}
              >
                <ListItemIcon><ScienceIcon /></ListItemIcon>
                <ListItemText primary="Exámenes" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to={`${base}/catalogo-medicamentos`}
                sx={navItemStyles}
                selected={location.pathname.startsWith(`${base}/catalogo-medicamentos`)}
              >
                <ListItemIcon><MedicationIcon /></ListItemIcon>
                <ListItemText primary="Medicamentos" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}

      {/* CAMBIO 1: Redes sociales horizontales en el footer */}
      <Box sx={{ marginTop: 'auto' }}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography
            variant="caption"
            sx={{ px: 1, mb: 1, color: 'text.secondary', fontWeight: 'medium', display: 'block' }}
          >
            Síguenos
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1, mb: 2 }}>
            <IconButton 
              component="a" 
              href="https://www.instagram.com/app.medifyhc" 
              target="_blank" 
              rel="noopener noreferrer"
              size="small"
            >
              <InstagramIcon fontSize="small" />
            </IconButton>
            <IconButton 
              component="a" 
              href="https://x.com/MedifyHC" 
              target="_blank" 
              rel="noopener noreferrer"
              size="small"
            >
              <XIcon fontSize="small" />
            </IconButton>
            <IconButton 
              component="a" 
              href="https://www.pinterest.com/MedifyHC" 
              target="_blank" 
              rel="noopener noreferrer"
              size="small"
            >
              <PinterestIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
            © {new Date().getFullYear()} MedifyHC
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw', bgcolor: 'background.default' }}>
      <CssBaseline />
      <SessionTimeout />

      {/* AppBar con sombra sutil y blur de fondo para elegancia */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: theme.shadows[3],
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'saturate(120%) blur(6px)',
          backgroundColor: alpha(theme.palette.primary.main, 0.98),
          transition: 'background-color 240ms ease, box-shadow 240ms ease',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 1,
              display: { sm: 'none' },
              borderRadius: '12px',
              transition: 'transform 160ms ease',
              '&:active': { transform: 'scale(0.96)' },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <RouterLink
            to={`${base}/dashboard`}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <Fade in timeout={300}>
              <img
                src={logo}
                alt="Logo Medify"
                style={{ height: '32px', marginRight: '8px', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' }}
              />
            </Fade>
            <Typography
              variant="h6"
              fontWeight="bold"
              noWrap
              sx={{ display: { xs: 'none', sm: 'block' }, letterSpacing: 0.4 }}
            >
              MEDIFY HEALTHCARE
            </Typography>
          </RouterLink>

          {/* Selector de clínica */}
          {ENABLE_CLINIC_SWITCHER && (
            <>
              {isMobile ? (
                <Tooltip title="Cambiar entidad">
                  <IconButton
                    color="inherit"
                    onClick={() => setMobileClinicSheetOpen(true)}
                    sx={{ ml: 1, borderRadius: '12px' }}
                    aria-label="switch clinic"
                  >
                    <LocalHospitalIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Box sx={{ ml: 1 }}>
                  <ClinicSwitcher />
                </Box>
              )}
            </>
          )}

          {/* Botón para crear clínica */}
          {ENABLE_CREATE_CLINIC && (
            <CreateClinicDialog
              asButton
              showIfNoClinics
              buttonProps={{
                size: 'small',
                startIcon: <AddBusinessIcon />,
                variant: 'outlined',
                color: 'secondary',
                sx: {
                  ml: 1,
                  borderColor: 'common.white',
                  color: 'common.white',
                  '&:hover': {
                    borderColor: 'common.white',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  letterSpacing: 0.2,
                }
              }}
            />
          )}

          {/* CAMBIO 2: Theme switcher con animación de rotación + pulse */}
          <Tooltip title={mode === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{ 
                ml: 2, 
                borderRadius: '12px', 
                transition: 'transform 0.3s ease',
                '&:hover': { 
                  transform: 'rotate(45deg)' 
                },
                '&:active': {
                  animation: `${pulse} 0.3s ease-in-out`
                }
              }}
              aria-label="toggle theme"
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {/* CAMBIO 4: Pill de clínica activa */}
          {activeClinic && (
            <Fade in timeout={220}>
              <Chip
                icon={<LocalHospitalIcon fontSize="small" />}
                label={activeClinic.clinica?.nombre || 'Clínica'}
                variant="outlined"
                size="small"
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  mr: 2,
                  borderColor: 'common.white',
                  color: 'common.white',
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  '& .MuiChip-icon': {
                    color: 'common.white'
                  }
                }}
              />
            </Fade>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: { xs: 1, sm: 2 }, opacity: 0.4, borderColor: 'rgba(255,255,255,0.5)' }} />
        

          {/* Chip de usuario */}
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            user && (
              <RouterLink to={`${base}/perfil`} style={{ textDecoration: 'none' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.5,
                    pr: 2,
                    borderRadius: '30px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                      borderColor: alpha(theme.palette.common.white, 0.3)
                    }
                  }}
                  >   
                      <Avatar
                        src={user?.medico?.foto_perfil || undefined}
                        alt={(user?.first_name || user?.firstName || 'U')}
                        sx={{ width: { xs: 24, sm: 36 }, height: { xs: 24, sm: 36 }, bgcolor: 'common.white', boxShadow: theme.shadows[2]  }}
                      >
                        {(user?.first_name || user?.firstName || 'U')?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Typography variant="body2" fontWeight={700} color="common.white">
                        {getDisplayName(user)}
                        </Typography>
                        <Typography variant="caption" display="block" color="common.white" sx={{ lineHeight: 1, opacity: 0.8 }}>
                          {getProfessionalTitle()}
                          </Typography>
                        </Box>
                      </Box>
                    </RouterLink>
                  )
                )}
      
          {/* CAMBIO 3: Botón logout en desktop - IconButton */}
          <Tooltip title="Cerrar Sesión">
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{
                ml: 2,
                borderRadius: '12px',
                display: { xs: 'none', sm: 'flex' },
                bgcolor: alpha(theme.palette.error.main, 0.15),
                color: theme.palette.error.light,
                '&:hover': { 
                  bgcolor: alpha(theme.palette.error.main, 0.25),
                  transform: 'translateY(-1px)'
                },
                transition: 'transform 150ms ease, background-color 180ms ease'
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>

          {/* Logout móvil */}
          <Tooltip title="Cerrar Sesión">
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{
                ml: 1,
                borderRadius: '12px',
                display: { xs: 'flex', sm: 'none' },
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                transition: 'transform 140ms ease',
                '&:active': { transform: 'scale(0.94)' }
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Bottom Sheet Móvil mejorado */}
      <Dialog
        open={mobileClinicSheetOpen}
        onClose={() => setMobileClinicSheetOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            margin: 0,
            borderRadius: '16px 16px 0 0',
            maxHeight: '74vh',
            boxShadow: theme.shadows[10],
            bgcolor: 'background.paper',
          }
        }}
        TransitionComponent={(props) => <Slide direction="up" {...props} />}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 2
            }}
          />
          Seleccionar Entidad
        </DialogTitle>
        <List2 sx={{ pt: 0 }}>
          <ListItemButton
            selected={selectedClinicId === null}
            onClick={() => {
              setSelectedClinicId(null);
              setMobileClinicSheetOpen(false);
            }}
            sx={{ transition: 'background-color 140ms ease' }}
          >
            <ListItemAvatar>
              <Avatar2><PersonIcon /></Avatar2>
            </ListItemAvatar>
            <ListItemText primary="Mi Cuenta Individual" />
            {selectedClinicId === null && <CheckIcon color="primary" />}
          </ListItemButton>

          {memberships?.map((membership) => (
            <ListItemButton
              key={membership.clinica.id}
              selected={selectedClinicId === membership.clinica.id}
              onClick={() => {
                setSelectedClinicId(membership.clinica.id);
                setMobileClinicSheetOpen(false);
              }}
              sx={{ transition: 'background-color 140ms ease' }}
            >
              <ListItemAvatar>
                <Avatar2><BusinessIcon /></Avatar2>
              </ListItemAvatar>
              <ListItemText primary={membership.clinica.nombre} />
              {selectedClinicId === membership.clinica.id && <CheckIcon color="primary" />}
            </ListItemButton>
          ))}
        </List2>
      </Dialog>

      {/* Drawer: temporal en móvil con animación, permanente en desktop */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        TransitionComponent={isMobile ? (props) => <Slide direction="right" {...props} /> : undefined}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            boxShadow: isMobile ? theme.shadows[8] : 'none', // NEW: solo móvil
            transition: 'box-shadow 200ms ease',
          },
        }}
      >
        {drawerContent}
      </Drawer>

        {/* Contenido principal con tarjeta virtual y scroll suave */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
            bgcolor: 'background.default',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <Toolbar />
          <Paper
            elevation={0}
            sx={{
              flexGrow: 1,
              width: '100%',
              overflowY: 'auto',
              p: { xs: 1, sm: 2 },
              borderRadius: { xs: 0, sm: 3 },
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
              transition: 'box-shadow 200ms ease, background-color 240ms ease',
              '&:hover': {
                boxShadow: theme.shadows[2],
              },
            }}
          >
            <Outlet />
          </Paper>
        </Box>
      </Box>
  );
};

export default MainLayout;
